import React, { useEffect, useRef, useState } from 'react';
import _debounce from 'lodash/debounce';
import { Collapse } from 'react-collapse';
import ContentLoader from 'react-content-loader';
import {
  ERROR_MESSAGE_REQUEST_ABORTED,
  ResourcePromise,
} from 'src/resource/Resource';
import { alertDialog } from 'src/web/components/Modals/AlertDialog';
import { FontAwesome } from 'src/web/components/FontAwesome';
import { Spinner } from 'src/web/components/Spinner/Spinner';
import {
  ROUTE_COURSE_PAGE,
  ROUTE_OTHER_PAGE,
} from 'src/ReduxStorage/routeActions';
import { RouteLink } from 'src/web/components/RouteLink/component';
import { IconButton } from 'src/web/components/Buttons/IconButton/IconButton';
import { Cohort } from 'src/ReduxStorage/Cohorts/CohortTypes';
import {
  courseCodeFromPath,
  institutionPrefixFromPath,
} from 'src/ReduxStorage/helpers/paths';
import { Course } from 'src/ReduxStorage/Courses/types';
import { slugFromCoursePath } from 'src/ReduxStorage/PageHelper/helpers';
import {
  CourseBlock,
  CourseInstitution,
  CourseName,
  CourseSlug,
  CourseDetails,
  FindNewCourseBlock,
  NoCourseMatchBlock,
  NoCourseMatchLabel,
  ClearAllIconWrapper,
  SearchTextWrapper,
  StyledTextField,
  LoadingBlock,
  CohortBlock,
  CohortContentLoaderWrapper,
  CourseList,
} from './styles';

type CourseListItem = {
  course: Course;
  isOpen: boolean;
  loadingCohorts: boolean;
  cohorts: Cohort[] | null;
};

let searchRequest: ResourcePromise<{ courses: Course[] }>;

export const SearchCourseMenu: React.FC<{
  isVisible: boolean;
  hasFetchedEnrolledClassData: boolean;
  onSearching: (searching: boolean) => void;
  searchForCourse: (
    searchString: string
  ) => ResourcePromise<{ courses: Course[] }>;
  getEnrolledCohorts: (coursePath: string) => Promise<Cohort[]>;
}> = ({
  hasFetchedEnrolledClassData,
  isVisible,
  onSearching,
  searchForCourse,
  getEnrolledCohorts,
}) => {
  const [searchString, setSearchString] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [courseListItems, setCourseListItems] = useState<CourseListItem[]>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const searchCourseDebounceRef = useRef(
    _debounce((searchString: string) => {
      if (searchRequest) {
        searchRequest.abort();
      }

      if (!searchString) {
        onSearching(false);
        setSearching(false);
        setCourseListItems([]);
      } else {
        onSearching(true);
        setSearching(true);
        setRequestLoading(true);
        searchRequest = searchForCourse(searchString);
        searchRequest.then(
          (result) => {
            setRequestLoading(false);
            setCourseListItems(
              result.courses.map((course) => ({
                course,
                isOpen: false,
                loadingCohorts: false,
                cohorts: null,
              }))
            );
          },
          (error) => {
            if (error.message !== ERROR_MESSAGE_REQUEST_ABORTED) {
              setRequestLoading(false);
              alertDialog({
                title: 'Error',
                message: 'Search failed, please try again',
              });
            }
          }
        );
      }
    }, 300)
  );

  useEffect((): void => {
    if (isVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchInputRef, isVisible]);

  const onChangeCourseSearchString = (searchString: string): void => {
    setSearchString(searchString);
    searchCourseDebounceRef.current(searchString);
  };

  const updateCourse = (item: CourseListItem): void => {
    setCourseListItems(
      courseListItems.map((x) => {
        if (x.course.path === item.course.path) {
          return item;
        }
        return x;
      })
    );
  };

  const toggleCourseOpen = async (item: CourseListItem): Promise<void> => {
    const newItem = { ...item };
    newItem.isOpen = !newItem.isOpen;

    // Load cohort
    if (item.cohorts == null && !item.loadingCohorts) {
      newItem.loadingCohorts = true;
      updateCourse(newItem);

      try {
        newItem.cohorts = await getEnrolledCohorts(newItem.course.path);
      } catch {
        alertDialog({
          title: 'Error',
          message: 'Getting cohort data failed, please try again',
        });
      } finally {
        newItem.loadingCohorts = false;
        updateCourse(newItem);
      }
    } else {
      updateCourse(newItem);
    }
  };

  return (
    <>
      <SearchTextWrapper>
        <StyledTextField
          icon="search"
          placeholder="Search for a course"
          value={searchString}
          focusOnMount
          onValueChange={(value: string) => {
            onChangeCourseSearchString(value);
          }}
        />
        <ClearAllIconWrapper
          hidden={searchString === '' || searchString == null}
        >
          <IconButton
            hiddenLabel="button"
            icon="remove"
            onClick={() => {
              onChangeCourseSearchString('');
              if (searchInputRef.current) {
                searchInputRef.current.focus();
              }
            }}
          />
        </ClearAllIconWrapper>
      </SearchTextWrapper>
      {searching &&
        (requestLoading ? (
          <LoadingBlock>
            <Spinner label="Searching my courses" />
          </LoadingBlock>
        ) : (
          <CourseList>
            {courseListItems.length > 0 ? (
              courseListItems.map((item) => (
                <div key={item.course.path}>
                  <CourseBlock onClick={() => toggleCourseOpen(item)}>
                    <CourseDetails>
                      <CourseName>{item.course.name}</CourseName>
                      <CourseInstitution>
                        {item.course.institution}
                      </CourseInstitution>
                      <CourseSlug>
                        {slugFromCoursePath(item.course.path)}
                      </CourseSlug>
                    </CourseDetails>
                    <FontAwesome
                      name={item.isOpen ? 'chevron-down' : 'chevron-right'}
                    />
                  </CourseBlock>
                  <Collapse isOpened={item.isOpen || false}>
                    {item.loadingCohorts ? (
                      <CohortContentLoaderWrapper>
                        <ContentLoader
                          speed={2}
                          width={200}
                          height={12}
                          viewBox="0 0 200 12"
                          backgroundColor="#f3f3f3"
                          foregroundColor="#ecebeb"
                        >
                          <rect
                            x="0"
                            y="0"
                            rx="3"
                            ry="3"
                            width="200"
                            height="12"
                          />
                        </ContentLoader>
                      </CohortContentLoaderWrapper>
                    ) : (
                      item.cohorts &&
                      item.cohorts.map((cohort) => (
                        <CohortBlock
                          to={{
                            type: ROUTE_COURSE_PAGE,
                            state: {
                              institutionPrefix:
                                institutionPrefixFromPath(item.course.path) ||
                                undefined,
                            },
                            params: {
                              courseCode:
                                courseCodeFromPath(item.course.path) || '',
                            },
                            query: {
                              inCohort: cohort.path,
                            },
                          }}
                          title={cohort.title}
                        >
                          {cohort.title}
                        </CohortBlock>
                      ))
                    )}
                  </Collapse>
                </div>
              ))
            ) : (
              <NoCourseMatchBlock>
                <NoCourseMatchLabel>
                  No courses match &quot;{searchString}&quot;
                </NoCourseMatchLabel>
                <FindNewCourseBlock>
                  <RouteLink
                    to={{
                      type: ROUTE_OTHER_PAGE,
                      params: {
                        prefix: 'search',
                      },
                      query: {
                        q: searchString,
                      },
                    }}
                  >
                    <FontAwesome name="search" /> Find new courses
                  </RouteLink>
                </FindNewCourseBlock>
              </NoCourseMatchBlock>
            )}
            {!hasFetchedEnrolledClassData && (
              <LoadingBlock narrow>
                <Spinner label="Loading more courses" />
              </LoadingBlock>
            )}
          </CourseList>
        ))}
    </>
  );
};
